import { ThreadVideoContext, ThreadVideoProvider } from "../../context/threadVideoContext";
import Body from "../common/Body";
import React, { useContext, useEffect, useCallback } from "react";
import { WidgetContext } from "../../context/widgetContext";
import HtmlParsedText from "../common/HtmlParsedText";
import { TITLE, VIDEO_DESCRIPTION, VIDEO_TITLE } from "../../config/config";
import LoadingContent from "../common/LoadingContent";
import LoadingNoMoreContent from "../common/LoadingNoMoreContent";
import VideoItem from "../Video/section/VideoItem";
import ErrorMessage from "../common/ErrorMessage"; // Assuming you have an error component

// Hero section component
const HeroContent = () => {
	const { thread } = useContext(ThreadVideoContext);

	return (
		<div className="px-2 pt-2 md:px-4 md:pt-4">
			<section className="flex items-center mb-2">
				<div>
					<h1 className="text-lg font-medium text-white">
						{thread ? thread.title : VIDEO_TITLE}
					</h1>
					<p className="text-sm text-url">
						{thread ? thread.excerpt : VIDEO_DESCRIPTION}
					</p>
				</div>
			</section>
		</div>
	);
};

// Sidebar widgets component
const SidenavContent = () => {
	const {
		forum_overview_members_online,
		tag_cloud,
		fetchWidgetHtml,
		widgetLoading
	} = useContext(WidgetContext);

	useEffect(() => {
		fetchWidgetHtml('forum_overview_members_online');
		fetchWidgetHtml('tag_cloud');
	}, [fetchWidgetHtml]);

	if (widgetLoading) {
		return <div className="p-2 text-white">Loading widgets...</div>;
	}

	return (
		<>
			{forum_overview_members_online && (
				<div className="mb-4">
					<HtmlParsedText text={forum_overview_members_online} />
				</div>
			)}

			{tag_cloud && (
				<div className="mb-4">
					<HtmlParsedText text={tag_cloud} />
				</div>
			)}
		</>
	);
};

// Video list component
const VideoList = ({ videos, loading, error, hasMore, onLoadMore }) => {
	if (error) {
		return <ErrorMessage message={error} />;
	}

	if (!videos.length && !loading) {
		return <LoadingNoMoreContent />;
	}

	return (
		<div>
			{videos.map((video) => (
				<div className="mb-4" key={video.video_id}>
					<VideoItem video={video} />
				</div>
			))}

			{loading && <LoadingContent />}
		</div>
	);
};

// Main content component
const MainContent = () => {
	const {
		thread,
		threadLoading,
		threadError,
		videosData,
		loadMoreVideos,
		fetchVideos
	} = useContext(ThreadVideoContext);

	// Update document metadata when thread data is available
	useEffect(() => {
		if (thread) {
			document.title = `Videos của ${thread.title} | ${VIDEO_TITLE} | ${TITLE}`;
			const metaDescription = document.querySelector('meta[name="description"]');
			if (metaDescription) {
				metaDescription.setAttribute("content", thread.excerpt);
			}
		}
	}, [thread]);

	// Initial video fetch
	useEffect(() => {
		if (thread && !videosData.initialLoaded) {
			fetchVideos(true);
		}
	}, [thread, videosData.initialLoaded, fetchVideos]);

	if (threadError) {
		return (
			<div className="p-4 text-red-500 text-center">
				Error loading thread: {threadError}
			</div>
		);
	}

	if (threadLoading || !thread) {
		return (
			<div className="p-4 text-white text-xl text-center">
				Đang tải...
			</div>
		);
	}

	return (
		<VideoList
			videos={videosData.videos}
			loading={videosData.loading}
			error={videosData.error}
			hasMore={videosData.hasMore}
			onLoadMore={loadMoreVideos}
		/>
	);
};

// Main component
const ThreadVideoIndex = () => {
	return (
		<ThreadVideoProvider>
			<Body
				heroContent={<HeroContent />}
				mainContent={<MainContent />}
				sidenavContent={<SidenavContent />}
			/>
		</ThreadVideoProvider>
	);
};

export default ThreadVideoIndex;